/**
 * Consent-gated queue for the standard Dreamdata pipeline, mirroring the pattern used for
 * Segment's own consent handling on the marketing site (jc-marketing-multisite, ti-2971).
 * Calls made before Dreamdata is ready are queued; calls made after consent is declined are
 * dropped. This only ever talks to the standard (cdn.dreamdata.cloud) pipeline - identify() must
 * never be sent to the cookieless pipeline, so it has no equivalent here.
 */
const READY_TIMEOUT_MS = 10000;

function executeDreamdataMethod(method, args) {
  if (!window.dreamdata) {
    return;
  }

  if (method === 'identify') {
    window.dreamdata.identify(...args);
  } else if (method === 'track') {
    window.dreamdata.track(...args);
  } else if (method === 'page') {
    window.dreamdata.page(...args);
  }
}

class DreamdataAwareUiEventsClass {
  constructor() {
    this.isDreamdataReady = false;
    this.eventQueue = [];
    this.consentDeclined = false;
    this.consentTimeout = null;
    // Ready timeout starts only in markConsentGranted - not on construct. Starting it here would
    // mark never-consented / cookieless-only sessions as declined after 10s and (historically)
    // blocked cookieless routing via the router.
  }

  startReadyTimeout() {
    clearTimeout(this.consentTimeout);
    this.consentTimeout = setTimeout(() => {
      if (!this.isDreamdataReady) {
        // Stuck SDK init after consent was granted (flag set but ready never fired).
        this.consentDeclined = true;
        this.clearQueue();
      }
    }, READY_TIMEOUT_MS);
  }

  /**
   * Called when OneTrust grants the required groups (including re-consent after revoke).
   * Clears any prior declined state so the queue can accept events again while waiting for ready.
   */
  markConsentGranted() {
    this.consentDeclined = false;
    this.isDreamdataReady = false;
    this.startReadyTimeout();
  }

  markDreamdataReady() {
    clearTimeout(this.consentTimeout);

    // Ready must not undo an explicit revoke; only markConsentGranted re-opens the queue.
    if (this.consentDeclined) {
      return;
    }

    this.isDreamdataReady = true;
    this.processQueue();
  }

  /**
   * Called only when consent was previously active and is now revoked - not for "not yet decided".
   */
  markConsentDeclined() {
    this.consentDeclined = true;
    this.isDreamdataReady = false;
    clearTimeout(this.consentTimeout);
    this.clearQueue();
  }

  processQueue() {
    while (this.eventQueue.length > 0) {
      const { method, args } = this.eventQueue.shift();
      try {
        executeDreamdataMethod(method, args);
      } catch (error) {
        // Swallow - Dreamdata must never break signup.
      }
    }
  }

  clearQueue() {
    this.eventQueue = [];
  }

  identify(...args) {
    this.queueOrExecute('identify', args);
  }

  track(...args) {
    this.queueOrExecute('track', args);
  }

  page(...args) {
    this.queueOrExecute('page', args);
  }

  queueOrExecute(method, args) {
    if (this.consentDeclined) {
      return;
    }

    if (this.isDreamdataReady) {
      try {
        executeDreamdataMethod(method, args);
      } catch (error) {
        // Swallow - Dreamdata must never break signup.
      }
    } else {
      this.eventQueue.push({ method, args });
    }
  }
}

let instance = null;

function DreamdataAwareUiEvents() {
  if (window.DreamdataUiEvents && typeof window.DreamdataUiEvents.identify === 'function') {
    return window.DreamdataUiEvents;
  }

  if (!instance) {
    instance = new DreamdataAwareUiEventsClass();
    if (window.DreamdataUiEvents && window.DreamdataUiEvents.isDreamdataReady) {
      instance.markDreamdataReady();
    }
  }

  return instance;
}

export default DreamdataAwareUiEvents;
